<template>
  <div class="embed-routes-panel" :class="{ collapsed: isCollapsed, docked }">
    <div class="panel-header" @click="toggleCollapse">
      <span class="panel-title">{{ hikeName || $t('hike_map_embed.embed_routes_panel.routes') }}</span>
      <button class="toggle-btn" type="button" :title="isCollapsed ? $t('hike_map_embed.embed_routes_panel.show_routes') : $t('hike_map_embed.embed_routes_panel.hide_routes')">
        <span class="material-icons">{{ isCollapsed ? 'expand_more' : 'expand_less' }}</span>
      </button>
    </div>

    <div class="panel-content" v-show="!isCollapsed">
      <div class="trail-list" v-if="plannedTrails && plannedTrails.length > 0">
        <!-- Show days if multi-day -->
        <template v-if="showDays">
          <div v-for="day in daysToShow" :key="`day-${day}`" class="day-section">
            <div class="day-header" @click="toggleDay(day)">
              <span class="day-title">{{ getDayName(day) }}</span>
              <span class="material-icons day-icon" :class="{ active: activeView === `day-${day}` }">
                {{ activeView === `day-${day}` ? 'radio_button_checked' : 'radio_button_unchecked' }}
              </span>
            </div>
            <div
              v-for="trail in getTrailsForDay(day)"
              :key="getTrailId(trail)"
              class="trail-item"
              :class="{ active: activeView === `route-${getTrailId(trail)}` }"
              @click="selectTrail(trail)"
            >
              <div class="trail-info">
                <div class="trail-title">{{ getTrailTitle(trail) }}</div>
                <div class="trail-stats">
                  {{ formatDistance(getTrailDistance(trail)) }} · {{ formatElevation(getTrailElevation(trail)) }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Simple list for single day -->
        <template v-else>
          <div
            v-for="(trail, index) in sortedTrails"
            :key="getTrailId(trail)"
            class="trail-item"
            :class="{ active: activeView === `route-${getTrailId(trail)}` }"
            @click="selectTrail(trail)"
          >
            <div class="trail-info">
              <div class="trail-title">{{ getTrailTitle(trail) }}</div>
              <div class="trail-stats">
                {{ formatDistance(getTrailDistance(trail)) }} · {{ formatElevation(getTrailElevation(trail)) }}
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="panel-footer">
        <button class="view-all-btn" @click="viewFullTrail" :class="{ active: activeView === 'full' }">
          <span class="material-icons">map</span>
          {{ $t('hike_map_embed.embed_routes_panel.view_all') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';

export default defineComponent({
  name: 'EmbedRoutesPanel',
  props: {
    hikeName: {
      type: String,
      default: ''
    },
    plannedTrails: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    hikeDays: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    // Stacked layout: a compact button in the title bar whose list drops down
    // over the map only while it's open.
    docked: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:collapsed', 'trail-selected', 'view-changed'],
  data() {
    return {
      isCollapsed: this.collapsed,
      activeView: 'full'
    };
  },
  computed: {
    hasJoinTableData(): boolean {
      if (this.plannedTrails.length === 0) return false;
      const firstTrail = this.plannedTrails[0];
      return firstTrail && 'day' in firstTrail && 'ptv2PlannedTrail' in firstTrail;
    },

    showDays(): boolean {
      if (!this.hasJoinTableData) return false;
      return this.plannedTrails.length > 1 && this.plannedTrails.some((t: any) => t.day != null);
    },

    sortedTrails(): any[] {
      const trails = [...this.plannedTrails];
      return trails.sort((a, b) => {
        const posA = this.hasJoinTableData ? (a.position || 0) : this.plannedTrails.indexOf(a);
        const posB = this.hasJoinTableData ? (b.position || 0) : this.plannedTrails.indexOf(b);
        return posA - posB;
      });
    },

    daysToShow(): number[] {
      if (!this.showDays) return [];
      const days = new Set<number>();
      this.plannedTrails.forEach((trail: any) => {
        if (trail.day != null) days.add(trail.day);
      });
      return Array.from(days).sort((a, b) => a - b);
    }
  },
  watch: {
    collapsed(newVal) {
      this.isCollapsed = newVal;
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('update:collapsed', this.isCollapsed);
    },

    getTrailId(trail: any): string | number {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.id || trail.id;
      }
      return trail.id;
    },

    getTrailTitle(trail: any): string {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.title || this.$t('hike_map_embed.embed_routes_panel.route_number', { number: trail.position });
      }
      return trail.title || this.$t('hike_map_embed.embed_routes_panel.route_number', { number: this.plannedTrails.indexOf(trail) + 1 });
    },

    getTrailDistance(trail: any): number {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.distanceMeters || 0;
      }
      return trail.distanceMeters || 0;
    },

    getTrailElevation(trail: any): number {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.elevationGainMeters || 0;
      }
      return trail.elevationGainMeters || 0;
    },

    getTrailsForDay(day: number): any[] {
      return this.sortedTrails.filter(trail => trail.day === day);
    },

    getDayName(dayNumber: number): string {
      const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === dayNumber);
      return dayInfo?.name || this.$t('hike_map_embed.embed_routes_panel.day_number', { number: dayNumber });
    },

    formatDistance(meters: number): string {
      const pref = MeasurementPrefUtility.preference();
      return pref.bigUnitFormatter(meters / 1000, true);
    },

    formatElevation(meters: number): string {
      const pref = MeasurementPrefUtility.preference();
      return pref.smallUnitFormatter(meters, false);
    },

    selectTrail(trail: any) {
      const trailId = this.getTrailId(trail);
      this.activeView = `route-${trailId}`;
      const plannedTrail = this.hasJoinTableData ? trail.ptv2PlannedTrail : trail;
      this.$emit('trail-selected', plannedTrail, this.sortedTrails.indexOf(trail));
      this.$emit('view-changed', { type: 'route', trailId });
    },

    toggleDay(dayNumber: number) {
      this.activeView = `day-${dayNumber}`;
      this.$emit('view-changed', { type: 'day', dayNumber });
    },

    viewFullTrail() {
      this.activeView = 'full';
      this.$emit('view-changed', { type: 'full' });
    }
  }
});
</script>

<style scoped>
.embed-routes-panel {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 220px;
  max-height: 280px;
  background: rgba(40, 42, 50, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  color: #fff;
  overflow: hidden;
}

.embed-routes-panel.collapsed {
  max-height: 36px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: rgba(30, 32, 38, 0.7);
  cursor: pointer;
  min-height: 36px;
  border-radius: 8px 8px 0 0;
}

.embed-routes-panel.collapsed .panel-header {
  border-radius: 8px;
}

.panel-title {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.toggle-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  padding: 2px;
  display: flex;
  cursor: pointer;
  border-radius: 4px;
}

.toggle-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.toggle-btn .material-icons {
  font-size: 18px;
}

.panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.trail-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

.day-section {
  margin-bottom: 4px;
}

.day-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background 0.2s;
}

.day-header:hover {
  background: rgba(255, 255, 255, 0.1);
}

.day-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}

.day-icon {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
}

.day-icon.active {
  color: #FFDF46;
}

.trail-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.trail-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.trail-item.active {
  background: rgba(255, 223, 70, 0.15);
  border-left: 3px solid #FFDF46;
  padding-left: 9px;
}

.trail-info {
  flex: 1;
  min-width: 0;
}

.trail-title {
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.trail-stats {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
}

.panel-footer {
  padding: 6px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 32, 38, 0.7);
}

.view-all-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: none;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
}

.view-all-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.view-all-btn.active {
  background: rgba(255, 223, 70, 0.2);
  color: #FFDF46;
}

.view-all-btn .material-icons {
  font-size: 14px;
}

/* Stacked layout */
.embed-routes-panel.docked,
.embed-routes-panel.docked.collapsed {
  position: relative;
  top: auto;
  left: auto;
  width: auto;
  max-height: none;
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: none;
  box-shadow: none;
  border-radius: 6px;
  overflow: visible;
}

.embed-routes-panel.docked .panel-header {
  min-height: 0;
  padding: 5px 6px 5px 10px;
  gap: 4px;
  background: transparent;
  border-radius: 6px;
}

.embed-routes-panel.docked .panel-content {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 240px;
  max-width: calc(100vw - 16px);
  max-height: 260px;
  background: rgba(40, 42, 50, 0.97);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  z-index: 200;
}

/* Scrollbar */
.trail-list::-webkit-scrollbar {
  width: 4px;
}

.trail-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.trail-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

/* Responsive */
@media (max-width: 480px) {
  .embed-routes-panel {
    width: 180px;
    max-height: 200px;
  }

  .panel-title {
    font-size: 11px;
  }

  .trail-title {
    font-size: 10px;
  }

  .trail-stats {
    font-size: 8px;
  }
}
</style>
